import { type ColorValue, type NumericValue, type PropertyTypeValueMap, StyleType } from '@wix/editor-platform-ooi-metadata-resolver';
import {
  createStylesParam,
  createStylesParams,
  type ISettingsColor,
  type ISettingsFont,
  type ISiteTextPreset,
  type IStyleParam,
  StyleParamType,
  wixColorParam,
  wixFontParam,
} from '@wix/tpa-settings';

export type IStylesParams = {
  backgroundColor: StyleParamType.Color;
  textFont: StyleParamType.Font;
  textColor: StyleParamType.Color;
  topAndBottomPadding: StyleParamType.Number;
  sidePadding: StyleParamType.Number;
  formFieldsGap: StyleParamType.Number;
  widgetBackgroundColor: StyleParamType.Color;
  selectionColor: StyleParamType.Color;
  dividerWidth: StyleParamType.Number;
  dividerColorAndOpacity: StyleParamType.Color;
  widgetBorderWidth: StyleParamType.Number;
  widgetBorderColorAndOpacity: StyleParamType.Color;
  widgetBorderRadius: StyleParamType.Number;
  formBorderWidth: StyleParamType.Number;
  formBorderColor: StyleParamType.Color;
  formBorderRadius: StyleParamType.Number;
  formFieldBackgroundColor: StyleParamType.Color;
  sectionTitleColor: StyleParamType.Color;
  sectionTitleFont: StyleParamType.Font;
  primaryTextColor: StyleParamType.Color;
  primaryTextFont: StyleParamType.Font;
  secondaryTextColor: StyleParamType.Color;
  secondaryTextFont: StyleParamType.Font;
  formFieldsLabelsColor: StyleParamType.Color;
  formFieldsLabelsFont: StyleParamType.Font;
  formFieldsInputTextColor: StyleParamType.Color;
  formFieldsInputTextFont: StyleParamType.Font;
  mobileServiceNameFontSize: StyleParamType.Number;
  mobileServiceTitlesFontSize: StyleParamType.Number;
  mobilePrimaryTextFontSize: StyleParamType.Number;
  mobileSecondaryTextFontSize: StyleParamType.Number;
  mobileFormFieldsLabelsFontSize: StyleParamType.Number;
  mobileFormFieldsInputsFontSize: StyleParamType.Number;
  mobileButtonSize: StyleParamType.Number;
  mainButtonTextColor: StyleParamType.Color;
  mainButtonFont: StyleParamType.Font;
  mainButtonBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonBorderColorAndOpacity: StyleParamType.Color;
  mainButtonBorderWidth: StyleParamType.Number;
  mainButtonBorderRadius: StyleParamType.Number;
  mainButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  mainButtonHoverColorAndOpacity: StyleParamType.Color;
  mainButtonHoverTextColor: StyleParamType.Color;
  secondaryButtonTextColor: StyleParamType.Color;
  secondaryButtonFont: StyleParamType.Font;
  secondaryButtonBackgroundColorAndOpacity: StyleParamType.Color;
  secondaryButtonBorderColorAndOpacity: StyleParamType.Color;
  secondaryButtonBorderWidth: StyleParamType.Number;
  secondaryButtonBorderRadius: StyleParamType.Number;
  secondaryButtonHoverBackgroundColorAndOpacity: StyleParamType.Color;
  secondaryButtonHoverColorAndOpacity: StyleParamType.Color;
  secondaryButtonHoverTextColor: StyleParamType.Color;
  formHeaderTwoFont: StyleParamType.Font;
  formHeaderTwoColor: StyleParamType.Color;
  formTextFont: StyleParamType.Font;
  formTextColor: StyleParamType.Color;
  pageTitleFont: StyleParamType.Font;
  pageTitleColor: StyleParamType.Color;
  serviceDetailsFont: StyleParamType.Font;
  serviceDetailsColor: StyleParamType.Color;
  secondaryTextFormFont: StyleParamType.Font;
  secondaryTextFormColor: StyleParamType.Color;
  disableColor: StyleParamType.Color;
};

type NumberStyleParam = Omit<
  IStyleParam<StyleParamType.Number, number>,
  'name'
>;

type StyleTypeFor<ValueType> = keyof {
  [K in keyof PropertyTypeValueMap as Required<PropertyTypeValueMap[K]> extends Required<ValueType> ? K : never]: K
};

const numberParam = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number | undefined,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: () => defaultValue as number,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'static',
      value: defaultValue as number,
    },
  },
});

const numberParamWithoutInheritDesktop = (
  type: StyleTypeFor<NumericValue>,
  defaultValue: number,
): NumberStyleParam => ({
  inheritDesktop: false,
  ...numberParam(type, defaultValue),
});

const numberRefParam = (
  type: StyleTypeFor<NumericValue>,
  otherParam: NumberStyleParam,
  otherParamName: string,
  fallback: number,
): NumberStyleParam => ({
  type: StyleParamType.Number,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherParam) ?? fallback,
  metadata: {
    type: type as StyleType.UNKNOWN_CssPropertyType, // type assertion is needed to avoid strange TypeScript error, because the enum union is too long
    defaultValue: {
      type: 'ref',
      ref: otherParamName,
      fallback: {
        type: 'static',
        value: fallback,
      },
    },
  },
});

type ColorStyleParam = Omit<
  IStyleParam<StyleParamType.Color, ISettingsColor>,
  'name'
>;

const colorParam = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
    },
  },
});

const colorRefParam = (
  type: StyleTypeFor<ColorValue>,
  otherColorParam: ColorStyleParam,
  otherColorParamName: string,
  fallbackPreset: string,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: ({ getStyleParamValue }) => getStyleParamValue(otherColorParam) || wixColorParam(fallbackPreset),
  metadata: {
    type,
    defaultValue: {
      type: 'ref',
      ref: otherColorParamName,
      fallback: {
        type: 'static',
        preset: fallbackPreset,
      },
    },
  },
});

const colorParamWithOpacity = (
  type: StyleTypeFor<ColorValue>,
  preset: string,
  opacity: number,
): ColorStyleParam => ({
  type: StyleParamType.Color,
  getDefaultValue: wixColorParam(preset, opacity),
  metadata: {
    type,
    defaultValue: {
      type: 'static',
      preset,
      opacity,
    },
  },
});

type FontStyleParam = Omit<
  IStyleParam<StyleParamType.Font, ISettingsFont>,
  'name'
>;

const fontParam = (
  preset: keyof ISiteTextPreset,
  props: { size?: number; htmlTag?: string },
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: wixFontParam(preset, props),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'static',
      preset,
      ...props,
    },
  },
});

const fontRefParam = (
  otherFontParam: FontStyleParam,
  otherFontParamName: string,
  fallbackPreset: keyof ISiteTextPreset,
  fallbackProps?: { size?: number; htmlTag?: string }
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontParam),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'ref',
      ref: otherFontParamName,
      fallback: {
        type: 'static',
        preset: fallbackPreset,
        ...fallbackProps,
      }
    },
  },
});

const fontRefParamWithMobileFallback = (
  otherFontParam: FontStyleParam,
  otherFontParamName: string,
  fallbackPreset: keyof ISiteTextPreset,
  mobileFallbackProps: { size?: number },
  fallbackProps: { size?: number }
): FontStyleParam => ({
  type: StyleParamType.Font,
  getDefaultValue: ({ getStyleParamValue }) =>
    getStyleParamValue(otherFontParam),
  metadata: {
    type: StyleType.font,
    defaultValue: {
      type: 'ref',
      ref: otherFontParamName,
      fallback: {
        type: 'static',
        preset: fallbackPreset,
        ...fallbackProps,
      }
    },
    mobileDefaultValue: {
      type: 'ref',
      ref: otherFontParamName,
      fallback: {
        type: 'static',
        preset: fallbackPreset,
        ...mobileFallbackProps,
      }
    },
  },
});

const widgetBackgroundColor = createStylesParam('widgetBackgroundColor', colorParam(StyleType.backgroundColor, 'color-1'));

const mainButtonBorderWidth = createStylesParam('mainButtonBorderWidth', numberParam(StyleType.borderWidth, 1));

const mainButtonBorderRadius = createStylesParam('mainButtonBorderRadius', numberParam(StyleType.borderRadius, 0));

const sectionTitleFont = createStylesParam('sectionTitleFont', fontParam('Page-title', { size: 20 }));

const sectionTitleColor = createStylesParam('sectionTitleColor', colorParam(StyleType.color, 'color-5'));

const primaryTextFont = createStylesParam('primaryTextFont', fontParam('Body-M', { size: 16 }));

const primaryTextColor = createStylesParam('primaryTextColor', colorParam(StyleType.color, 'color-5'));

const secondaryTextFont = createStylesParam('secondaryTextFont', fontParam('Body-M', { size: 14 }));

const secondaryTextColor = createStylesParam('secondaryTextColor', colorParam(StyleType.color, 'color-4'));

export default createStylesParams<IStylesParams>({
  formHeaderTwoFont: fontRefParam(sectionTitleFont, 'sectionTitleFont', 'Body-M', { size: 20 }),
  formHeaderTwoColor: colorRefParam(StyleType.color, sectionTitleColor, 'sectionTitleColor', 'color-5'),
  formTextFont:  fontRefParamWithMobileFallback(primaryTextFont, 'primaryTextFont', 'Body-M', { size: 16 }, { size: 14 }),
  formTextColor: colorRefParam(StyleType.color, primaryTextColor, 'primaryTextColor', 'color-5'),
  pageTitleFont: fontParam('Page-title', { size: 32 }),
  pageTitleColor: colorParam(StyleType.color, 'color-5'),
  serviceDetailsFont: fontRefParam(sectionTitleFont, 'sectionTitleFont', 'Page-title', { size: 20 }),
  serviceDetailsColor: colorRefParam(StyleType.color, sectionTitleColor, 'sectionTitleColor', 'color-5'),
  secondaryTextFormFont: fontRefParam(secondaryTextFont, 'secondaryTextFont', 'Body-M', { size: 14 }),
  secondaryTextFormColor: colorRefParam(StyleType.color, secondaryTextColor, 'secondaryTextColor', 'color-text-secondary'),
  disableColor: colorParamWithOpacity(StyleType.color, 'button-color-fill-primary-disabled', 1),
  backgroundColor: colorParam(StyleType.backgroundColor, 'color-3'),
  textFont: fontParam('Body-M', { size: 20 }),
  textColor: colorParam(StyleType.color, 'color-5'),
  topAndBottomPadding: numberParamWithoutInheritDesktop(StyleType.padding, 48),
  sidePadding: numberParamWithoutInheritDesktop(StyleType.padding, 20),
  formFieldsGap: numberParamWithoutInheritDesktop(StyleType.gap, 24),
  widgetBackgroundColor,
  selectionColor: colorParam(StyleType.color, 'color-8'),
  dividerWidth: numberParam(StyleType.width, 1),
  dividerColorAndOpacity: colorParamWithOpacity(StyleType.color, 'color-5', 0.2),
  widgetBorderWidth: numberParam(StyleType.borderWidth, 0),
  widgetBorderColorAndOpacity: colorParamWithOpacity(StyleType.borderColor, 'color-5', 0.2),
  widgetBorderRadius: numberParam(StyleType.borderRadius, 0),
  formBorderWidth: numberParam(StyleType.borderWidth, 1),
  formBorderColor: colorParam(StyleType.borderColor, 'color-5'),
  formBorderRadius: numberParam(StyleType.borderRadius, 0),
  formFieldBackgroundColor: colorRefParam(StyleType.backgroundColor, widgetBackgroundColor, 'widgetBackgroundColor', 'color-1'),
  sectionTitleFont,
  sectionTitleColor,
  primaryTextFont,
  primaryTextColor,
  secondaryTextColor,
  secondaryTextFont,
  formFieldsLabelsColor: colorParam(StyleType.color, 'color-5'),
  formFieldsLabelsFont: fontParam('Body-M', { size: 16 }),
  formFieldsInputTextColor: colorParam(StyleType.color, 'color-5'),
  formFieldsInputTextFont: fontParam('Body-M', { size: 16 }),
  mobileServiceNameFontSize: numberParam(StyleType.fontSize, 32),
  mobileServiceTitlesFontSize: numberParam(StyleType.fontSize, 20),
  mobilePrimaryTextFontSize: numberParam(StyleType.fontSize, 16),
  mobileSecondaryTextFontSize: numberParam(StyleType.fontSize, 14),
  mobileFormFieldsLabelsFontSize: numberParam(StyleType.fontSize, 14),
  mobileFormFieldsInputsFontSize: numberParam(StyleType.fontSize, 14),
  mobileButtonSize: numberParam(StyleType.fontSize, 14),
  mainButtonTextColor: colorParam(StyleType.color, 'color-1'),
  mainButtonFont: fontParam('Body-M', { size: 16 }),
  mainButtonBackgroundColorAndOpacity: colorParam(StyleType.backgroundColor, 'color-8'),
  mainButtonBorderColorAndOpacity: colorParam(StyleType.borderColor, 'color-8'),
  mainButtonBorderWidth,
  mainButtonBorderRadius,
  mainButtonHoverBackgroundColorAndOpacity: colorParamWithOpacity(StyleType.backgroundColor, 'color-8', 1),
  mainButtonHoverColorAndOpacity: colorParamWithOpacity(StyleType.color, 'color-8', 1),
  mainButtonHoverTextColor: colorParam(StyleType.color, 'color-1'),
  secondaryButtonTextColor: colorParam(StyleType.color, 'color-1'),
  secondaryButtonFont: fontParam('Body-M', { size: 16 }),
  secondaryButtonBackgroundColorAndOpacity: colorParam(StyleType.backgroundColor, 'color-5'),
  secondaryButtonBorderColorAndOpacity: colorParam(StyleType.borderColor, 'color-5'),
  secondaryButtonBorderWidth: numberRefParam(StyleType.borderWidth, mainButtonBorderWidth, 'mainButtonBorderWidth', 1),
  secondaryButtonBorderRadius: numberRefParam(StyleType.borderRadius, mainButtonBorderRadius, 'mainButtonBorderRadius', 0),
  secondaryButtonHoverBackgroundColorAndOpacity: colorParamWithOpacity(StyleType.backgroundColor, 'color-5', 1),
  secondaryButtonHoverColorAndOpacity: colorParamWithOpacity(StyleType.borderColor, 'color-5', 1),
  secondaryButtonHoverTextColor: colorParam(StyleType.color, 'color-1'),
});
